import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MailSettings } from 'src/app/infrastructure/auth/user-tenant';
import { SnackbarService } from 'src/app/infrastructure/service/snackbar.service';
import {
  EmailPreferencesService,
  PublicEmailPreferences
} from './email-preferences.service';

@Component({
  selector: 'app-email-preferences',
  templateUrl: './email-preferences.component.html',
  styleUrls: ['./email-preferences.component.scss'],
  standalone: false
})
export class EmailPreferencesComponent implements OnInit {

  formGroup: UntypedFormGroup;
  token = '';
  loading = true;
  saving = false;
  loadError = false;
  preferences?: PublicEmailPreferences;
  mailSettingsOptions = Object.values(MailSettings);

  constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private service: EmailPreferencesService,
    private snackbar: SnackbarService,
    private translate: TranslateService
  ) {
    this.formGroup = this.fb.group({
      mailSettings: [MailSettings.ALL, Validators.required],
      commercialMailEnabled: [false]
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.token = params.get('token') || '';
      if (!this.token) {
        this.loading = false;
        this.loadError = true;
        return;
      }

      this.loadPreferences();
    });
  }

  async save(): Promise<void> {
    if (!this.token || this.formGroup.invalid || this.saving) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.saving = true;

    try {
      await this.service.updateByToken(this.token, {
        mailSettings: this.formGroup.get('mailSettings')?.value,
        commercialMailEnabled: !!this.formGroup.get('commercialMailEnabled')?.value
      });

      this.snackbar.success(this.translate.instant('emailPreferences.success'));
      this.preferences = {
        ...(this.preferences || {} as PublicEmailPreferences),
        token: this.token,
        mailSettings: this.formGroup.get('mailSettings')?.value,
        commercialMailEnabled: !!this.formGroup.get('commercialMailEnabled')?.value
      };
    } catch (err: any) {
      const message = err?.error?.message || this.translate.instant('emailPreferences.error');
      this.snackbar.error(message);
    } finally {
      this.saving = false;
    }
  }

  private async loadPreferences(): Promise<void> {
    this.loading = true;
    this.loadError = false;

    try {
      this.preferences = await this.service.getByToken(this.token);
      this.formGroup.patchValue({
        mailSettings: this.preferences.mailSettings || MailSettings.ALL,
        commercialMailEnabled: !!this.preferences.commercialMailEnabled
      });
    } catch (err) {
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }
}
